import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
  Input,
  Row,
  Col,
} from "reactstrap";

function normalizeEmail(email) {
  return String(email || "").trim().toLowerCase();
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function parentDisplayName(row) {
  const first = String(row?.firstName || "").trim();
  const last = String(row?.lastName || "").trim();
  return `${first} ${last}`.trim();
}

function parentEmail(row) {
  return String(row?.email || "").trim().toLowerCase();
}

function hasPersonName(row) {
  return !!(String(row?.firstName || "").trim() || String(row?.lastName || "").trim());
}

function buildUniquePeopleRows({ referralParents, programs, isReferral }) {
  const byEmail = new Map();

  const upsert = (email, candidate) => {
    const key = normalizeEmail(email);
    if (!key) return;

    const existing = byEmail.get(key);
    if (!existing) {
      const firstPaidAt = candidate.firstPaidAt || null;
      byEmail.set(key, {
        ...candidate,
        email: key,
        isPaid: !!(candidate.isPaid || firstPaidAt),
        firstPaidAt,
        paymentDate: firstPaidAt,
      });
      return;
    }

    const existingCreated = new Date(existing.createdAt || 0).getTime();
    const nextCreated = new Date(candidate.createdAt || 0).getTime();
    let base = nextCreated < existingCreated ? candidate : existing;
    if (!hasPersonName(base) && hasPersonName(existing)) base = existing;
    if (!hasPersonName(base) && hasPersonName(candidate)) base = candidate;

    const existingPaidAt = existing.firstPaidAt
      ? new Date(existing.firstPaidAt).getTime()
      : Infinity;
    const nextPaidAt = candidate.firstPaidAt
      ? new Date(candidate.firstPaidAt).getTime()
      : Infinity;
    const bestPaidAt =
      Math.min(existingPaidAt, nextPaidAt) === Infinity
        ? null
        : new Date(Math.min(existingPaidAt, nextPaidAt)).toISOString();

    const packageTitle =
      bestPaidAt &&
      candidate.firstPaidAt &&
      new Date(candidate.firstPaidAt).getTime() === new Date(bestPaidAt).getTime()
        ? candidate.packageTitle || existing.packageTitle || "—"
        : existing.packageTitle || candidate.packageTitle || "—";

    byEmail.set(key, {
      ...base,
      firstName: base.firstName || existing.firstName || candidate.firstName || "",
      lastName: base.lastName || existing.lastName || candidate.lastName || "",
      country: base.country || existing.country || candidate.country || "",
      email: key,
      isPaid: !!(existing.isPaid || candidate.isPaid || bestPaidAt),
      firstPaidAt: bestPaidAt,
      paymentDate: bestPaidAt,
      packageTitle,
    });
  };

  if (Array.isArray(referralParents)) {
    for (const parent of referralParents) {
      const firstPaidAt = parent.firstPaidAt || parent.paymentDate || null;
      upsert(parent.email, {
        id: parent.id,
        firstName: parent.firstName,
        lastName: parent.lastName,
        email: parent.email,
        country: parent.country,
        signupSource: parent.signupSource || (isReferral ? "referral" : "affiliate"),
        createdAt: parent.createdAt,
        isPaid: !!(parent.isPaid || parent.hasPaid || firstPaidAt),
        firstPaidAt,
        paymentDate: firstPaidAt,
        packageTitle: parent.packageTitle || "—",
      });
    }
  }

  if (Array.isArray(programs)) {
    for (const prog of programs) {
      const parent = prog?.child?.parent || prog?.parent;
      const email = parent?.email || prog?.parentEmail || prog?.email || null;
      if (!email) continue;
      const firstPaidAt =
        prog.firstPaidAt ||
        prog.paymentDate ||
        (prog.isPaid && prog.createdAt ? prog.createdAt : null);
      upsert(email, {
        id: parent?.id || prog.id,
        firstName: parent?.firstName || "",
        lastName: parent?.lastName || "",
        email,
        country: parent?.country || prog?.country || "",
        signupSource: isReferral ? "referral" : "affiliate",
        createdAt: parent?.createdAt || prog.createdAt,
        isPaid: !!prog.isPaid || !!firstPaidAt,
        firstPaidAt,
        paymentDate: firstPaidAt,
        packageTitle: prog?.cohort?.title || prog?.package?.title || "—",
      });
    }
  }

  return Array.from(byEmail.values()).sort((a, b) => {
    const aTime = new Date(a.createdAt || 0).getTime();
    const bTime = new Date(b.createdAt || 0).getTime();
    return bTime - aTime;
  });
}

const StatusBadge = ({ isPaid }) => (
  <span
    className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill"
    style={{
      fontSize: 12,
      fontWeight: 600,
      background: isPaid ? "#ecfdf5" : "#fef2f2",
      color: isPaid ? "#047857" : "#b91c1c",
      border: `1px solid ${isPaid ? "#a7f3d0" : "#fecaca"}`,
    }}
  >
    <span
      style={{
        width: 7,
        height: 7,
        borderRadius: 99,
        background: isPaid ? "#10b981" : "#ef4444",
        display: "inline-block",
      }}
    />
    {isPaid ? "Paid" : "Unpaid"}
  </span>
);

const StatCard = ({ label, value, tone }) => (
  <Col xs={6} lg={3}>
    <div
      className="h-100 rounded-3 p-3"
      style={{
        background: "#fff",
        border: "1px solid #f0e4ec",
      }}
    >
      <div
        className="text-uppercase mb-2"
        style={{ fontSize: 11, letterSpacing: "0.06em", color: "#94a3b8", fontWeight: 600 }}
      >
        {label}
      </div>
      <div className="fw-bold mb-0" style={{ color: tone, fontSize: 28, lineHeight: 1 }}>
        {value}
      </div>
    </div>
  </Col>
);

const ReferralUsageModal = ({ isOpen, toggle, loading, error, usage }) => {
  const [people, setPeople] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [search, setSearch] = useState("");

  const isReferral = !!usage?.isReferral || !!usage?.summary?.isReferral;

  useEffect(() => {
    if (!isOpen) {
      setPeople([]);
      setFilteredData([]);
      setSelectedFilter("all");
      setSearch("");
      return;
    }
    const referralParents = Array.isArray(usage?.referralParents) ? usage.referralParents : [];
    const programs = Array.isArray(usage?.programs) ? usage.programs : [];
    const rows = buildUniquePeopleRows({
      referralParents,
      programs,
      isReferral,
    });
    setPeople(rows);
    setSelectedFilter("all");
    setSearch("");
    setFilteredData(rows);
  }, [isOpen, usage, isReferral]);

  const applyFilters = (statusValue, searchValue, source = people) => {
    let next = [...source];
    if (statusValue === "paid") {
      next = next.filter((item) => !!item.isPaid);
    } else if (statusValue === "unpaid") {
      next = next.filter((item) => !item.isPaid);
    }
    const q = String(searchValue || "").trim().toLowerCase();
    if (q) {
      next = next.filter((item) => {
        const hay = `${item.firstName || ""} ${item.lastName || ""} ${
          item.email || ""
        } ${item.country || ""}`.toLowerCase();
        return hay.includes(q);
      });
    }
    setFilteredData(next);
  };

  const stats = useMemo(() => {
    const paid = people.filter((item) => !!item.isPaid).length;
    return {
      total: people.length,
      paid,
      unpaid: Math.max(0, people.length - paid),
      shown: filteredData.length,
    };
  }, [people, filteredData]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable>
      <ModalHeader toggle={toggle}>
        {isReferral ? "Referral conversions" : "Affiliate conversions"}
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <div className="mt-2 text-muted">Loading usage…</div>
          </div>
        ) : error ? (
          <div className="alert alert-danger mb-0">{error}</div>
        ) : (
          <>
            <div className="mb-3">
              <p className="mb-1" style={{ color: "#64748b", fontSize: 14 }}>
                Code{" "}
                <span
                  className="badge"
                  style={{ background: "#f6ebf3", color: "#aa468e", fontSize: 13 }}
                >
                  {usage?.code || "—"}
                </span>
                {usage?.agentName ? (
                  <>
                    {" "}
                    · Agent <strong>{usage.agentName}</strong>
                  </>
                ) : null}
                {usage && !usage.isActive ? (
                  <>
                    {" "}
                    · <span className="badge bg-secondary">Inactive</span>
                  </>
                ) : null}
              </p>
              <p className="mb-0" style={{ color: "#94a3b8", fontSize: 12 }}>
                One row per email · paid only after checkout with this code
              </p>
            </div>

            <Row className="g-3 mb-4">
              <StatCard label="Unique people" value={stats.total} tone="#aa468e" />
              <StatCard label="Paid" value={stats.paid} tone="#047857" />
              <StatCard label="Unpaid" value={stats.unpaid} tone="#b91c1c" />
              <StatCard label="Showing" value={stats.shown} tone="#1d4ed8" />
            </Row>

            <div
              className="rounded-3 overflow-hidden"
              style={{ border: "1px solid #f0e4ec" }}
            >
              <div style={{ borderBottom: "1px solid #f3e8ef", background: "#fcfafb" }}>
                <div className="px-3 pt-3 pb-2">
                  <h5 className="fw-semibold mb-0" style={{ color: "#334155", fontSize: 16 }}>
                    {isReferral ? "Parents by referral code" : "Parents by affiliate code"}
                  </h5>
                </div>
                <div className="px-3 pb-3">
                  <Row className="g-2 align-items-end">
                    <Col md={8}>
                      <label
                        htmlFor="referral-usage-search"
                        className="form-label mb-1"
                        style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}
                      >
                        Search
                      </label>
                      <Input
                        id="referral-usage-search"
                        type="search"
                        placeholder="Search by name, email, or country"
                        value={search}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSearch(value);
                          applyFilters(selectedFilter, value);
                        }}
                      />
                    </Col>
                    <Col md={4}>
                      <label
                        htmlFor="referral-usage-status"
                        className="form-label mb-1"
                        style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}
                      >
                        Payment status
                      </label>
                      <Input
                        id="referral-usage-status"
                        type="select"
                        value={selectedFilter}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSelectedFilter(value);
                          applyFilters(value, search);
                        }}
                      >
                        <option value="all">All</option>
                        <option value="paid">Paid</option>
                        <option value="unpaid">Unpaid</option>
                      </Input>
                    </Col>
                  </Row>
                </div>
              </div>

              <div className="table-responsive mb-0">
                <table className="table align-middle mb-0" style={{ fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#faf7fa" }}>
                      <th className="border-0 px-3 py-3 text-uppercase" style={{ fontSize: 11, color: "#94a3b8" }}>
                        #
                      </th>
                      <th className="border-0 px-3 py-3 text-uppercase" style={{ fontSize: 11, color: "#94a3b8" }}>
                        Parent
                      </th>
                      <th className="border-0 px-3 py-3 text-uppercase" style={{ fontSize: 11, color: "#94a3b8" }}>
                        Email
                      </th>
                      <th className="border-0 px-3 py-3 text-uppercase" style={{ fontSize: 11, color: "#94a3b8" }}>
                        Country
                      </th>
                      <th className="border-0 px-3 py-3 text-uppercase" style={{ fontSize: 11, color: "#94a3b8" }}>
                        Signed up
                      </th>
                      <th className="border-0 px-3 py-3 text-uppercase" style={{ fontSize: 11, color: "#94a3b8" }}>
                        First payment
                      </th>
                      <th className="border-0 px-3 py-3 text-uppercase" style={{ fontSize: 11, color: "#94a3b8" }}>
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.length > 0 ? (
                      filteredData.map((row, i) => {
                        const name = parentDisplayName(row);
                        const email = parentEmail(row);
                        return (
                          <tr key={row.id || row.email || i} style={{ borderTop: "1px solid #f5eef3" }}>
                            <td className="px-3 py-3" style={{ color: "#94a3b8" }}>
                              {i + 1}
                            </td>
                            <td className="px-3 py-3">
                              <div className="fw-semibold" style={{ color: "#0f172a" }}>
                                {name || email || "—"}
                              </div>
                              {email ? (
                                <div style={{ color: "#64748b", fontSize: 12, wordBreak: "break-all" }}>
                                  {email}
                                </div>
                              ) : null}
                              {row.packageTitle && row.packageTitle !== "—" ? (
                                <div style={{ color: "#94a3b8", fontSize: 12 }}>{row.packageTitle}</div>
                              ) : null}
                            </td>
                            <td className="px-3 py-3" style={{ color: "#475569", wordBreak: "break-all" }}>
                              {email || "—"}
                            </td>
                            <td className="px-3 py-3" style={{ color: "#475569" }}>
                              {row.country || "—"}
                            </td>
                            <td className="px-3 py-3" style={{ color: "#475569" }}>
                              {formatDate(row.createdAt)}
                            </td>
                            <td className="px-3 py-3">
                              {row.isPaid ? (
                                <span className="fw-medium" style={{ color: "#0f172a" }}>
                                  {formatDate(row.firstPaidAt || row.paymentDate)}
                                </span>
                              ) : (
                                <span style={{ color: "#94a3b8" }}>—</span>
                              )}
                            </td>
                            <td className="px-3 py-3">
                              <StatusBadge isPaid={!!row.isPaid} />
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={7} className="text-center py-5" style={{ color: "#64748b" }}>
                          {people.length === 0
                            ? isReferral
                              ? "No parents have signed up with this referral code yet."
                              : "No parent activity for this affiliate code yet."
                            : "No records match the selected filters."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ReferralUsageModal;
